<template>
    <VDialog :open="modelValue" :headline="selectedId ? 'Edit Webhook' : 'Add Webhook'" modal @close="close()" style="--dialog-min-inline-size: 550px; --dialog-max-block-size: 85vh;" :full-width-body="true">
        <template #body>
            <div class="modal-container">
                <div v-if="savingLoader" class="overlay">
                    <VProgressRing connotation="cta" />
                </div>

                <p class="modal-description ion-no-margin">Specify the webhook URL to receive real-time event notifications.</p>

                <div class="field">
                    <div class="sub-title vpb-10">Webhook URL<span class="req">*</span></div>
                    <div class="method-url-row">
                        <VSelect class="method-select" v-model="form.method" appearance="ghost" fixed-dropdown>
                           <VOption value="HTTPS POST" text="HTTPS POST" />
                        </VSelect>
                        <VTextField
                            class="url-input"
                            type="url"
                            placeholder="https://example.com/fallback"
                            v-model="form.url"
                            appearance="ghost"
                            :class="{ invalid: urlError }"
                        />
                    </div>
                    <div v-if="urlError" class="error-text">Please enter a valid HTTPS URL.</div>
                </div>

                <div class="field">
                    <div class="field-label">Scopes <span class="req">*</span></div>
                    <div class="field-help">Select the scopes that determine what data and actions your app can perform.</div>
                    <div v-if="loading || editWebhookLoader" class="spinner">
                        <VProgressRing connotation="cta" />
                    </div>
                    <div v-else class="scopes-list">
                        <div v-for="sec in sections" :key="sec.key" class="scope-section">
                            <div class="scope-header" @click="toggleSection(sec.key)">
                                <span class="scope-title">{{ sec.title }}</span>
                                <VIcon :name="expandedSections.includes(sec.key) ? 'chevron-up-line' : 'chevron-down-line'" class="chev" />
                            </div>
                            <div v-if="expandedSections.includes(sec.key)" class="scope-content">
                                <div class="scope-row select-all" @click="toggleAll(sec, !isAllChecked(sec))">
                                    <VCheckbox :checked="isAllChecked(sec)" connotation="cta" label="Select All" />
                                </div>
                                <div v-for="scope in sec.scopes" :key="scope.key" class="scope-row" @click="toggleOne(sec, scope.key, !checked[sec.key].has(scope.key))">
                                    <VCheckbox :checked="checked[sec.key].has(scope.key)" connotation="cta">
                                        <span class="scope-pill">{{ scope.label }}</span>
                                    </VCheckbox>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #action-items>
            <VButton v-if="selectedId" class="vvd-btn-secondary" connotation="cta" appearance="outlined" :label="verifyWebhookLoader ? 'Verifying...' : 'Verify webhook'" :disabled="loading || form.url === '' || editWebhookLoader || savingLoader" @click="testWebHook" />
            <VButton class="vvd-btn-cancel" appearance="filled" label="Cancel" :disabled="savingLoader" @click="close()" />
            <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Save" :disabled="loading || form.url === '' || editWebhookLoader || savingLoader" @click="handleSaveClick()" />
        </template>
    </VDialog>
</template>

<script>
import apiInterceptor from "@/shared/apiInterceptor";
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env.js';

export default {
    name: "AddWebhookModal",
    props: {
        modelValue: { type: Boolean, required: true },
        onSave: { type: Function, required: true },
        viewWebhookScopesWithId: { type: Function, required: true },
        fetchWebhooksList: { type: Function, required: true },
        webhookUrl: { type: String, default: "" },
        selectedScopes: { type: Object, default: () => ({}) },
        selectedId: { type: String, default: null },
    },
    emits: ["update:modelValue"],
    data() {
        return {
            urlError: false,
            form: {
                method: "HTTPS POST",
                url: "",
                scopes: {},
            },
            sections: [],
            checked: {},
            expandedSections: [],
            loading: false,
            editWebhookLoader: false,
            savingLoader: false,
            verifyWebhookLoader: false,
        };
    },
    methods: {
        close() {
            this.$emit("update:modelValue", false);
            this.resetForm();
        },
        toggleSection(key) {
            const idx = this.expandedSections.indexOf(key);
            if (idx > -1) this.expandedSections.splice(idx, 1);
            else this.expandedSections.push(key);
        },
        isValidHttps(url) {
            if (!url) return false;
            const httpsPattern = /^https:\/\/[^\s/$.?#].[^\s]*$/i;
            if (!httpsPattern.test(url)) return false;
            try {
                const { hostname } = new URL(url);
                if (!/^[a-zA-Z0-9._-]+$/.test(hostname)) return false;
                if (!hostname.includes(".")) return false;
            } catch (e) { return false; }
            return true;
        },
        getSelectedScopeIds() {
            if (!this.checked) return [];
            return Object.values(this.checked).flatMap((set) => Array.from(set));
        },
        showToast(message, type = "danger") {
            useUIStore().updateToast({ open: true, message, type });
        },
        extractErrorMessage(error, fallback) {
            const data = error?.response?.data;
            if (!data) return error?.message || fallback;
            if (typeof data.detail === "string") return data.detail;
            if (Array.isArray(data.detail) && data.detail.length) {
                return data.detail[0]?.msg || data.detail[0]?.message || fallback;
            }
            return data.message || data.apiMessage || fallback;
        },
        async handleSaveClick() {
            this.urlError = !this.isValidHttps(this.form.url);
            if (this.urlError) return;
            const scopeIds = this.getSelectedScopeIds();
            if (scopeIds.length === 0) {
                this.showToast("Please select at least one scope");
                return;
            }
            const isEdit = !!this.selectedId;
            this.savingLoader = true;
            try {
                await this.onSave({ ...this.form, selectedScope: scopeIds, edit: isEdit });
            } catch (error) {
                this.showToast(this.extractErrorMessage(error, "Something went wrong, please try again"));
                this.savingLoader = false;
                return;
            } finally {
                this.savingLoader = false;
            }
            this.close();
            this.fetchWebhooksList();
            this.showToast(isEdit ? "Webhook edited successfully" : "Webhook added successfully", "success");
        },
        isAllChecked(sec) {
            return this.checked[sec.key].size === sec.scopes.length && sec.scopes.length > 0;
        },
        toggleAll(sec, checked) {
            const set = this.checked[sec.key];
            set.clear();
            if (checked) sec.scopes.forEach((s) => set.add(s.key));
            this.$forceUpdate();
        },
        toggleOne(sec, key, checked) {
            const set = this.checked[sec.key];
            checked ? set.add(key) : set.delete(key);
            this.$forceUpdate();
        },
        async getWebHookScopes() {
            try {
                this.loading = true;
                const response = await apiInterceptor.get(environment("VUE_APP_APIGEE_API_URL") + "metadata/products/scopes");
                const scopesData = response.scopes || {};
                this.sections = Object.entries(scopesData).map(([key, list]) => ({
                    key,
                    title: key.charAt(0).toUpperCase() + key.slice(1),
                    scopes: list.map((item) => ({ key: item.id, label: item.scope })),
                }));
                this.checked = Object.keys(scopesData).reduce((acc, key) => { acc[key] = new Set(); return acc; }, {});
            } catch (error) {
                this.showToast(this.extractErrorMessage(error, "Something went wrong, please try again"));
            } finally {
                this.loading = false;
            }
        },
        resetForm() {
            this.form = { method: "HTTPS POST", url: "", scopes: {} };
            this.sections = [];
            this.checked = {};
            this.expandedSections = [];
            this.urlError = false;
        },
        async getWebHookScopesFromId(id) {
            try {
                this.editWebhookLoader = true;
                if (!this.modelValue) return;
                if (!this.sections.length) await this.getWebHookScopes();
                const resp = await this.viewWebhookScopesWithId(id);
                const selectedIds = new Set(Array.isArray(resp) ? resp.map((s) => s.id) : (resp?.scopes ?? []).map((s) => s.id));
                for (const sec of this.sections) {
                    const set = this.checked[sec.key] ?? new Set();
                    set.clear();
                    for (const scope of sec.scopes) {
                        if (selectedIds.has(scope.key)) set.add(scope.key);
                    }
                    this.checked[sec.key] = set;
                }
            } catch (error) {
                this.showToast(this.extractErrorMessage(error, "Something went wrong, please try again"));
            } finally {
                this.editWebhookLoader = false;
            }
        },
        async testWebHook() {
            this.urlError = !this.isValidHttps(this.form.url);
            if (this.urlError) return;
            this.savingLoader = true;
            this.verifyWebhookLoader = true;
            try {
                const response = await apiInterceptor.post(environment("VUE_APP_APIGEE_API_URL") + "webhooks/validate", { url: this.form.url });
                this.showToast(response?.apiMessage || "Webhook URL validation successful", "success");
            } catch (error) {
                this.showToast(this.extractErrorMessage(error, "Something went wrong"));
                this.urlError = true;
            } finally {
                this.savingLoader = false;
                this.verifyWebhookLoader = false;
            }
        }
    },
    watch: {
        modelValue: {
            async handler(val) {
                if (this.webhookUrl) this.form.url = this.webhookUrl;
                if (this.selectedId) {
                    await this.getWebHookScopes();
                    this.getWebHookScopesFromId(this.selectedId);
                } else {
                    if (val) this.getWebHookScopes();
                }
            },
            immediate: true,
        },
    },
};
</script>

<style scoped lang="scss">
.overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 12px;
    z-index: 10;
}
.modal-container {
    position: relative;
    padding: 0 24px;
}
.modal-description {
    font-size: 14px;
    color: var(--black);
    margin-bottom: 16px;
}
.field { margin-bottom: 16px; }
.field-label { font-weight: 400; margin-bottom: 10px; font-size: 14px; }
.req { color: red; margin-left: 2px; }
.error-text { color: red; font-size: 12px; margin-top: 4px; }
.field-help { font-size: 14px; color: var(--gray-04); margin-bottom: 8px; }
.method-url-row { display: flex; align-items: stretch; gap: 10px; }
.method-select { width: 160px; background: var(--background-primary); border-radius: 5px; min-height: 40px; }
.url-input { flex: 1; background: var(--background-primary); border-radius: 5px; min-height: 40px; }
.url-input.invalid { outline: 1px solid red; border-radius: 5px; }
.spinner { display: flex; align-items: center; justify-content: center; min-height: 100px; }
.scopes-list { max-height: 300px; overflow-y: auto; margin-top: 10px; }
.scope-section { border-bottom: 1px solid var(--background-primary); }
.scope-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 0; cursor: pointer;
    &:hover { background: var(--background-primary); }
}
.scope-title { font-size: 16px; font-weight: 500; color: var(--black); }
.chev { font-size: 18px; color: var(--gray-04); }
.scope-content { padding: 8px 10px 12px; }
.scope-row {
    padding: 6px 0; border-bottom: 1px solid var(--background-primary); cursor: pointer;
    &:last-child { border-bottom: none; }
    &:hover { background: var(--background-primary); }
}
.scope-row.select-all { font-weight: 500; }
.scope-pill {
    display: inline-block; font-size: 14px; color: var(--orange);
    background: var(--background-primary); border-radius: 6px; padding: 6px 10px; line-height: 1;
}
</style>
